// ABOUTME: Analyzes wardrobe data to produce scores, neglected items, and gap insights
// ABOUTME: Pure functions consuming items, logs, and outfits for analytics display

import type { Outfit, WardrobeItem } from '@/lib/store/wardrobeStore'
import type { WearLog } from '@/lib/store/wearLogStore'

export interface WardrobeScore {
  overall: number
  variety: number
  utilization: number
  condition: number
  balance: number
}

export interface NeglectedItem {
  item: WardrobeItem
  daysSinceWorn: number | null
}

export interface WardrobeGap {
  category: string
  description: string
  impact: string
}

export interface UsageStats {
  mostWorn: WardrobeItem[]
  leastWorn: WardrobeItem[]
  neverWorn: WardrobeItem[]
  utilizationRate: number
}

const ALL_CATEGORIES = ['tops', 'bottoms', 'shoes', 'outerwear', 'accessories'] as const
const FORMALITY_LEVELS = ['casual', 'smart-casual', 'formal'] as const
const CONDITION_SCORES: Record<string, number> = { excellent: 100, good: 75, fair: 50 }

function daysBetween(dateStr: string, now: Date): number {
  const date = new Date(dateStr)
  return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
}

function getLastWornDate(itemId: string, logs: WearLog[]): string | null {
  let latest: string | null = null
  for (const log of logs) {
    if (log.itemIds.includes(itemId)) {
      if (!latest || log.date > latest) latest = log.date
    }
  }
  return latest
}

function getWearCount(itemId: string, logs: WearLog[]): number {
  return logs.filter((l) => l.itemIds.includes(itemId)).length
}

export function calculateWardrobeScore(
  items: WardrobeItem[],
  logs: WearLog[],
  _outfits: Outfit[]
): WardrobeScore {
  if (items.length === 0) {
    return { overall: 0, variety: 0, utilization: 0, condition: 0, balance: 0 }
  }

  const variety = calculateVarietyScore(items)
  const utilization = calculateUtilizationScore(items, logs)
  const condition = calculateConditionScore(items)
  const balance = calculateBalanceScore(items)

  const overall = Math.round(variety * 0.25 + utilization * 0.3 + condition * 0.2 + balance * 0.25)

  return { overall, variety, utilization, condition, balance }
}

function calculateVarietyScore(items: WardrobeItem[]): number {
  const categoryCounts: Record<string, number> = {}
  for (const item of items) {
    categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1
  }

  const populatedCategories = Object.keys(categoryCounts).length
  const categoryRatio = populatedCategories / ALL_CATEGORIES.length

  const total = items.length
  const maxPerCategory = Math.max(...Object.values(categoryCounts))
  const evenness = 1 - (maxPerCategory / total - 1 / populatedCategories)

  return Math.round(Math.min(100, categoryRatio * 60 + Math.max(0, evenness) * 40))
}

function calculateUtilizationScore(items: WardrobeItem[], logs: WearLog[]): number {
  if (logs.length === 0) return 0

  const now = new Date()
  const thirtyDaysAgo = new Date(now)
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const cutoff = thirtyDaysAgo.toISOString().split('T')[0]

  const recentLogs = logs.filter((l) => l.date >= cutoff)
  const recentlyWornIds = new Set<string>()
  for (const log of recentLogs) {
    for (const id of log.itemIds) {
      recentlyWornIds.add(id)
    }
  }

  const rate = (recentlyWornIds.size / items.length) * 100
  return Math.round(Math.min(100, rate))
}

function calculateConditionScore(items: WardrobeItem[]): number {
  const total = items.reduce((sum, item) => sum + (CONDITION_SCORES[item.condition] || 50), 0)
  return Math.round(total / items.length)
}

function calculateBalanceScore(items: WardrobeItem[]): number {
  let score = 50

  const formalityCounts: Record<string, number> = {}
  for (const item of items) {
    if (item.formality) {
      formalityCounts[item.formality] = (formalityCounts[item.formality] || 0) + 1
    }
  }

  const hasFormalityVariety = Object.keys(formalityCounts).length >= 2
  if (hasFormalityVariety) score += 25

  const seasonCounts: Record<string, number> = {}
  for (const item of items) {
    if (item.season) {
      seasonCounts[item.season] = (seasonCounts[item.season] || 0) + 1
    }
  }

  const hasSeasonVariety = Object.keys(seasonCounts).length >= 2
  if (hasSeasonVariety) score += 25

  return Math.min(100, score)
}

export function getNeglectedItems(
  items: WardrobeItem[],
  logs: WearLog[],
  thresholdDays: number = 30
): NeglectedItem[] {
  const now = new Date()
  const result: NeglectedItem[] = []

  for (const item of items) {
    const lastWorn = getLastWornDate(item.id, logs)

    if (!lastWorn) {
      result.push({ item, daysSinceWorn: null })
    } else {
      const days = daysBetween(lastWorn, now)
      if (days >= thresholdDays) {
        result.push({ item, daysSinceWorn: days })
      }
    }
  }

  return result.sort((a, b) => {
    if (a.daysSinceWorn === null && b.daysSinceWorn === null) return 0
    if (a.daysSinceWorn === null) return -1
    if (b.daysSinceWorn === null) return 1
    return b.daysSinceWorn - a.daysSinceWorn
  })
}

export function identifyGaps(items: WardrobeItem[], _outfits: Outfit[]): WardrobeGap[] {
  const gaps: WardrobeGap[] = []

  const categoryCounts: Record<string, number> = {}
  for (const item of items) {
    categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1
  }

  for (const cat of ALL_CATEGORIES) {
    if (!categoryCounts[cat]) {
      gaps.push({
        category: cat,
        description: `No ${cat} in your wardrobe`,
        impact: `Adding ${cat} would expand your outfit options`,
      })
    }
  }

  const formalityCounts: Record<string, number> = {}
  for (const item of items) {
    if (item.formality) {
      formalityCounts[item.formality] = (formalityCounts[item.formality] || 0) + 1
    }
  }

  for (const level of FORMALITY_LEVELS) {
    if (!formalityCounts[level] && items.length > 0) {
      gaps.push({
        category: 'formality',
        description: `No ${level} items tagged`,
        impact: `${level.charAt(0).toUpperCase() + level.slice(1)} pieces expand where you can go`,
      })
    }
  }

  return gaps
}

export function getUsageStats(items: WardrobeItem[], logs: WearLog[]): UsageStats {
  if (items.length === 0) {
    return { mostWorn: [], leastWorn: [], neverWorn: [], utilizationRate: 0 }
  }

  const wearCounts = new Map<string, number>()
  for (const item of items) {
    wearCounts.set(item.id, getWearCount(item.id, logs))
  }

  const neverWorn = items.filter((i) => (wearCounts.get(i.id) || 0) === 0)

  const worn = items
    .filter((i) => (wearCounts.get(i.id) || 0) > 0)
    .sort((a, b) => (wearCounts.get(b.id) || 0) - (wearCounts.get(a.id) || 0))

  const mostWorn = worn.slice(0, 5)
  const leastWorn = worn.slice(-5).reverse()

  const now = new Date()
  const thirtyDaysAgo = new Date(now)
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const cutoff = thirtyDaysAgo.toISOString().split('T')[0]

  const recentLogs = logs.filter((l) => l.date >= cutoff)
  const recentlyWornIds = new Set<string>()
  for (const log of recentLogs) {
    for (const id of log.itemIds) {
      recentlyWornIds.add(id)
    }
  }

  const utilizationRate = Math.round((recentlyWornIds.size / items.length) * 100)

  return { mostWorn, leastWorn, neverWorn, utilizationRate }
}

export function getOutfitPotential(items: WardrobeItem[]): number {
  const tops = items.filter((i) => i.category === 'tops').length
  const bottoms = items.filter((i) => i.category === 'bottoms').length
  const shoes = items.filter((i) => i.category === 'shoes').length

  if (tops === 0 || bottoms === 0) return 0

  if (shoes === 0) return tops * bottoms

  return tops * bottoms * shoes
}
